import { memo, useEffect } from "react";
import { Handle, useUpdateNodeInternals, type NodeProps } from "@xyflow/react";
import { GitMerge } from "lucide-react";
import { cn } from "@/lib/utils";
import type { HubNodeData } from "@/types/decision-graph";
import type { DecisionMode, EmitRule, ScoreBand } from "@/types/decision-definition-schema";
import { getHandlePositions } from "../utils/handle-positions";
import {
  getDistributedHandleOffset,
  getHubOutputHandles,
} from "../utils/hub-handles";

export const HubNode = memo(function HubNode({
  id,
  data,
  selected,
}: NodeProps) {
  const d = data as HubNodeData & { emitRules?: EmitRule[]; scoreBands?: ScoreBand[] };
  const updateNodeInternals = useUpdateNodeInternals();
  const { isHorizontal, inputPosition, outputPosition } = getHandlePositions(
    d.layoutDirection
  );

  const mode = (d.decisionMode || "tri_state") as DecisionMode;
  const outputHandles = getHubOutputHandles(mode, d.emitRules, d.scoreBands);

  // Handles are added/removed when mode changes — notify React Flow
  useEffect(() => {
    updateNodeInternals(id);
  }, [id, mode, outputHandles.length, updateNodeInternals]);

  return (
    <div
      className={cn(
        "px-5 py-4 rounded-xl border-2 bg-white shadow-sm min-w-[120px]",
        "transition-all duration-150",
        selected
          ? "border-purple-500 shadow-md ring-2 ring-purple-200"
          : "border-purple-300 hover:border-purple-400"
      )}
    >
      <Handle
        type="target"
        position={inputPosition}
        id="input"
        style={{ background: "#a855f7", width: 10, height: 10 }}
      />

      {outputHandles.map((handle, index) => (
        <Handle
          key={handle.id}
          type="source"
          position={outputPosition}
          id={handle.id}
          title={handle.label}
          style={{
            ...getDistributedHandleOffset(isHorizontal, index, outputHandles.length),
            background: handle.color,
            width: 8,
            height: 8,
          }}
        />
      ))}

      <div className="flex flex-col items-center gap-1">
        <GitMerge size={20} className="text-purple-600" />
        <span className="text-xs font-bold text-purple-700">Decision</span>
        <span
          className={cn(
            "px-2 py-0.5 rounded text-[10px] font-medium",
            "bg-purple-100 text-purple-700 border border-purple-200"
          )}
        >
          {mode === "boolean"
            ? "Boolean"
            : mode === "tri_state"
              ? "Tri-State"
              : mode === "multi_outcome"
                ? "Multi"
                : "Scorecard"}
        </span>
        {outputHandles.length > 0 && (
          <div className="mt-1 flex flex-wrap justify-center gap-1">
            {outputHandles.map((h) => (
              <span
                key={h.id}
                className="rounded px-1 py-px text-[9px] font-medium text-white"
                style={{ background: h.color }}
              >
                {h.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
});
